import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import SopFlow from "@/components/crm/SopFlow";
import SopEditor from "@/components/crm/SopEditor";
import SopCanvas from "@/components/crm/SopCanvas";
import { useConfirm } from "@/components/ConfirmDialog";
import { Plus, Pencil, Eye, Copy, Trash2, Download, Upload, Radio } from "lucide-react";
import { toast } from "sonner";

const err = (e, f) => toast.error(e.response?.data?.detail || f);

function LiveFor({ live, modules, sops, isAdmin, onSet }) {
  const active = sops.filter((s) => s.status === "active");
  const mods = [{ _id: "leads", name: "Leads pipeline" }, ...modules];
  return (
    <div className="panel p-3 flex flex-wrap items-center gap-3 text-xs" data-testid="sop-live-for">
      <span className="flex items-center gap-1 text-neutral-400"><Radio size={12} />Live SOP per module</span>
      {mods.map((m) => (
        <label key={m._id} className="flex items-center gap-1.5">{m.name}
          <select className="px-2 py-1 rounded border text-xs" disabled={!isAdmin} value={live[m._id] || ""} onChange={(e) => onSet(m._id, e.target.value || null)} data-testid={`sop-live-${m._id}`}>
            {m._id !== "leads" && <option value="">none</option>}
            {active.map((s) => <option key={s._id} value={s._id}>{s.name} · v{s.version}</option>)}
          </select>
        </label>
      ))}
      <span className="text-neutral-600">A form ending without its own "Send lead to" uses the Leads pipeline SOP.</span>
    </div>
  );
}

export default function SopsPanel({ isAdmin }) {
  const { confirm } = useConfirm();
  const [rows, setRows] = useState([]);
  const [live, setLive] = useState({});
  const [modules, setModules] = useState([]);
  const [sel, setSel] = useState("ad-leads");
  const [draft, setDraft] = useState(null);
  const [edit, setEdit] = useState(false);
  const [mode, setMode] = useState(() => localStorage.getItem("sop_mode") || "lanes");
  const pickMode = (m) => { setMode(m); localStorage.setItem("sop_mode", m); };
  const [forms, setForms] = useState([]);
  const [newName, setNewName] = useState("");
  const fileRef = useRef();
  const importMode = useRef("new");
  const load = () => api.get("/crm/sops").then((r) => { setRows(r.data.rows); setLive(r.data.live || {}); });
  useEffect(() => {
    load();
    api.get("/forms").then((r) => setForms(r.data.rows || r.data || [])).catch(() => {});
    api.get("/crm/modules").then((r) => setModules(r.data.rows)).catch(() => {});
  }, []);
  const sop = rows.find((r) => r._id === sel) || rows[0];
  useEffect(() => { setDraft(null); setEdit(false); }, [sel]);
  const entries = useMemo(() => forms.flatMap((f) => (f.settings?.endings || [])
    .filter((e) => (e.route || (e.outcome === "disqualified" ? "none" : `sop:${live.leads || "ad-leads"}`)) === `sop:${sop?._id}`)
    .map((e) => `${f.title} → ${e.title?.slice(0, 40) || e.id}`)), [forms, sop, live]);
  if (!sop) return <div className="text-xs text-neutral-500">Loading…</div>;
  const cur = draft || sop;
  const liveIn = Object.entries(live).filter(([, v]) => v === sop._id).map(([k]) => (k === "leads" ? "Leads pipeline" : modules.find((m) => m._id === k)?.name || k));

  const save = async (body = draft, msg = "SOP saved") => {
    try { await api.put(`/crm/sops/${sop._id}`, body); toast.success(msg); setDraft(null); setEdit(false); load(); } catch (e) { err(e, "Save failed"); }
  };
  const create = async (copy) => {
    const name = copy ? `${sop.name} copy` : newName.trim();
    if (!name) return;
    const { data } = await api.post("/crm/sops", { name, copy_from: sop._id });
    setNewName(""); await load(); setSel(data._id); toast.success("SOP created (inactive)");
  };
  const del = async () => {
    if (!(await confirm({ title: `Delete ${sop.name}?`, danger: true }))) return;
    try { await api.delete(`/crm/sops/${sop._id}`); setSel("ad-leads"); load(); } catch (e) { err(e, "Delete failed"); }
  };
  const setLiveFor = async (module, sop_id) => {
    try { const { data } = await api.put("/crm/sop-live", { module, sop_id }); setLive(data.live); toast.success("Live SOP updated"); } catch (e) { err(e, "Failed"); }
  };
  const exportCsv = async () => {
    const r = await api.get(`/crm/sops/${sop._id}/export`, { responseType: "blob" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = `sop-${sop._id}-v${sop.version}.csv`; a.click();
  };
  const pickImport = (mode) => { importMode.current = mode; fileRef.current.click(); };
  const onImport = async (f) => {
    if (!f) return;
    const text = await f.text();
    fileRef.current.value = "";
    const replace = importMode.current === "replace";
    if (replace && !(await confirm({ title: `Replace ${sop.name} with this CSV?`, body: "The current version is kept in history.", okLabel: "Replace" }))) return;
    try {
      const { data } = await api.post("/crm/sops/import", { csv: text, sop_id: replace ? sop._id : null });
      await load(); setSel(data._id); toast.success(replace ? `Updated to v${data.version}` : `Imported "${data.name}" (inactive)`);
    } catch (e) { err(e, "Import failed"); }
  };
  return (
    <div className="space-y-4" data-testid="sops-panel">
      <LiveFor live={live} modules={modules} sops={rows} isAdmin={isAdmin} onSet={setLiveFor} />
      <div className="flex flex-wrap items-center gap-2">
        {rows.map((r) => (
          <button key={r._id} onClick={() => setSel(r._id)} data-testid={`sop-pick-${r._id}`}
            className={`px-3 py-1 rounded-full border text-xs transition-colors ${r._id === sop._id ? "bg-white text-black border-white" : r.status === "active" ? "text-neutral-300 hover:text-white" : "text-neutral-400 border-dashed hover:text-neutral-200"}`}>
            {r.name}{r.status !== "active" && " · inactive"}
          </button>
        ))}
        {isAdmin && <span className="flex items-center gap-1 ml-2">
          <input className="px-2 py-1 rounded border text-xs w-36" placeholder="New SOP name" value={newName} onChange={(e) => setNewName(e.target.value)} data-testid="sop-new-name" />
          <button className="btn text-xs" onClick={() => create(false)} disabled={!newName.trim()} data-testid="sop-create-btn"><Plus size={12} />New</button>
        </span>}
      </div>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="text-sm font-medium">{sop.name}</span>
        <span className="pill" data-testid="sop-version">v{sop.version}</span>
        <button type="button" className="text-xs font-mono text-[var(--text-dim)] hover:text-[var(--text)] px-1.5 py-0.5 rounded border border-[var(--border)]" title="sop_id: click to copy"
          onClick={() => { navigator.clipboard?.writeText(sop._id); toast.success(`Copied sop_id: ${sop._id}`); }} data-testid="sop-id-copy">sop_id: {sop._id}</button>
        <span className={`pill ${sop.status === "active" ? "text-emerald-400" : "text-neutral-500"}`} data-testid="sop-status">{sop.status}</span>
        {liveIn.length > 0 && <span className="pill text-sky-300" data-testid="sop-live-badge">Live: {liveIn.join(", ")}</span>}
        <span className="ml-auto flex flex-wrap items-center gap-1.5">
          <button className="btn text-xs" onClick={exportCsv} data-testid="sop-export-btn"><Download size={12} />CSV</button>
          {isAdmin && !edit && <>
            <button className="btn text-xs" onClick={() => pickImport("new")} data-testid="sop-import-new-btn"><Upload size={12} />Import new</button>
            <button className="btn text-xs" onClick={() => pickImport("replace")} data-testid="sop-import-replace-btn"><Upload size={12} />Replace from CSV</button>
            <button className="btn text-xs" onClick={() => save({ ...sop, status: sop.status === "active" ? "inactive" : "active" }, sop.status === "active" ? "Deactivated" : "Activated")} data-testid="sop-status-toggle">{sop.status === "active" ? "Deactivate" : "Activate"}</button>
            <button className="btn text-xs" onClick={() => create(true)} data-testid="sop-duplicate-btn"><Copy size={12} />Duplicate</button>
            {sop._id !== "ad-leads" && !liveIn.length && <button className="btn text-xs hover:text-[var(--breach)]" onClick={del} data-testid="sop-delete-btn"><Trash2 size={12} /></button>}
          </>}
          {isAdmin && <button className="btn text-xs" onClick={() => { setEdit(!edit); setDraft(null); }} data-testid="sop-edit-toggle">{edit ? <><Eye size={12} />View</> : <><Pencil size={12} />Edit</>}</button>}
          {(edit || (mode === "canvas" && draft)) && <button className="btn btn-primary text-xs" onClick={() => save()} disabled={!draft} data-testid="sop-save-btn">{draft ? `Save as v${sop.version + 1}` : "Saved"}</button>}
          {!edit && mode === "canvas" && draft && <button className="btn text-xs" onClick={() => setDraft(null)} data-testid="sop-discard-btn">Discard</button>}
        </span>
        <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => onImport(e.target.files[0])} data-testid="sop-import-file" />
      </div>
      <div className="flex items-center gap-3">
        <div className="text-xs text-neutral-500 flex-1">{cur.description}</div>
        <div className="flex rounded-md border border-[var(--border-strong)] overflow-hidden text-xs" data-testid="sop-mode">
          {[["lanes", "Lanes + form"], ["canvas", "Canvas"]].map(([m, l]) => (
            <button key={m} onClick={() => pickMode(m)} data-testid={`sop-mode-${m}`}
              className={`px-3 py-1 transition-colors ${mode === m ? "bg-[var(--panel-2)] text-[var(--text)] font-medium" : "text-[var(--text-dim)] hover:text-[var(--text)]"}`}>{l}</button>
          ))}
        </div>
      </div>
      {mode === "canvas" ? <SopCanvas sop={cur} onChange={setDraft} editable={isAdmin} /> : <>
        <SopFlow sop={cur} entries={entries} />
        {edit && <SopEditor sop={cur} onChange={setDraft} />}
      </>}
    </div>
  );
}
